import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, Eye, EyeOff, Briefcase, ArrowLeft, CheckCircle2, MailCheck, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../App';
import PublicLayout from '../components/public/PublicLayout';
import SearchableSelect from '../components/SearchableSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR', 'PKR', 'BDT', 'NPR', 'NGN', 'AED'];
const GENDERS = ['Male', 'Female', 'Other'];

const RegisterPage = () => {
  const [params] = useSearchParams();
  const initialTab = params.get('as') === 'agent' ? 'agent' : 'student';
  const [tab, setTab] = useState(initialTab); // student | agent
  const [countries, setCountries] = useState([]);
  const [universities, setUniversities] = useState([]);
  const navigate = useNavigate();

  // Sync tab when URL ?as= changes (e.g. user clicks the header Register dropdown
  // again while already on /register)
  useEffect(() => {
    const desired = params.get('as') === 'agent' ? 'agent' : 'student';
    setTab(desired);
  }, [params]);

  useEffect(() => {
    axios.get(`${API}/public/master-data?type=country`).then((r) => setCountries(r.data || [])).catch(() => {});
    axios.get(`${API}/public/master-data?type=university`).then((r) => setUniversities(r.data || [])).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      <section className="bg-gradient-to-br from-[#F5A623]/5 to-white py-10 sm:py-14">
        <div className="max-w-5xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A2A4F] hover:text-[#F5A623] mb-6" data-testid="register-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-10">
            <h1 className="text-2xl sm:text-3xl font-bold text-center text-[#1A2A4F]">
              {tab === 'student' ? 'Create your free account' : 'Create Agent Account'}
            </h1>

            {/* Student / Agent toggle */}
            <div className="mt-6 mb-2 inline-flex rounded-lg bg-gray-100 p-1" data-testid="register-tabs">
              <button
                type="button"
                onClick={() => setTab('student')}
                className={`px-5 py-2 text-sm font-bold rounded-md transition flex items-center gap-2 ${tab === 'student' ? 'bg-white text-[#F5A623] shadow' : 'text-gray-600 hover:text-[#1A2A4F]'}`}
                data-testid="register-tab-student"
              >
                <GraduationCap className="w-4 h-4" /> Student
              </button>
              <button
                type="button"
                onClick={() => setTab('agent')}
                className={`px-5 py-2 text-sm font-bold rounded-md transition flex items-center gap-2 ${tab === 'agent' ? 'bg-white text-[#F5A623] shadow' : 'text-gray-600 hover:text-[#1A2A4F]'}`}
                data-testid="register-tab-agent"
              >
                <Briefcase className="w-4 h-4" /> Agent
              </button>
            </div>
            <p className="text-xs text-gray-500">*required information</p>

            {tab === 'student'
              ? <StudentForm countries={countries} onSuccess={() => { window.location.href = '/'; }} />
              : <AgentForm universities={universities} countries={countries} />
            }
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

const Input = ({ label, required, type = 'text', value, onChange, placeholder, hint, testid }) => (
  <div>
    <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">{label}{required && ' *'}</label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none"
      data-testid={testid}
    />
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const Select = ({ label, required, value, onChange, options, placeholder, testid }) => {
  const opts = (options || []).map((o) => (typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: o.label || o.value }));
  return (
    <div>
      <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">{label}{required && ' *'}</label>
      <SearchableSelect
        value={value}
        onChange={onChange}
        options={opts}
        required={required}
        placeholder={placeholder}
        testid={testid}
      />
    </div>
  );
};

// ─────────────── Reusable OTP block (used by both Student + Agent forms) ───────────────
const OtpStep = ({ email, otp, setOtp, sent, setSent, error, setError }) => {
  const [sending, setSending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const send = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address above before sending the code.');
      return;
    }
    setSending(true); setError('');
    try {
      await axios.post(`${API}/ebn/auth/send-otp`, { email, purpose: 'register' });
      setSent(true);
      setSecondsLeft(60);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send verification code. Please try again.');
    }
    setSending(false);
  };

  return (
    <fieldset className={`rounded-xl border ${sent ? 'border-[#F5A623]/30 bg-[#F5A623]/5' : 'border-gray-100 bg-gray-50/50'} p-5 transition`} data-testid="otp-step">
      <legend className="px-2 -ml-2 text-sm font-bold text-[#1A2A4F] inline-flex items-center gap-2">
        <MailCheck className="w-4 h-4 text-[#F5A623]" /> Verify your email
      </legend>
      {!sent ? (
        <>
          <p className="text-sm text-gray-600">We'll send a 6-digit code to <strong>{email || 'your email'}</strong> to confirm your address.</p>
          <button
            type="button"
            onClick={send}
            disabled={sending || !email}
            className="mt-3 bg-[#1A2A4F] text-white text-sm font-semibold px-5 py-2.5 rounded-md hover:opacity-90 disabled:opacity-50 transition inline-flex items-center gap-2"
            data-testid="otp-send-btn"
          >
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : 'Send verification code'}
          </button>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-700">A 6-digit code was sent to <strong>{email}</strong>. Enter it below to finish creating your account.</p>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              className="w-full sm:w-48 border border-gray-200 rounded-md px-3 py-2.5 text-center text-lg tracking-[8px] font-bold text-[#1A2A4F] focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none"
              data-testid="otp-code-input"
            />
            <button
              type="button"
              onClick={send}
              disabled={sending || secondsLeft > 0}
              className="text-sm font-semibold text-[#F5A623] hover:underline disabled:opacity-50 disabled:no-underline inline-flex items-center gap-1.5"
              data-testid="otp-resend-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> {secondsLeft > 0 ? `Resend in ${secondsLeft}s` : 'Resend code'}
            </button>
          </div>
        </>
      )}
    </fieldset>
  );
};

const MultiSelect = ({ label, required, values, onChange, options, placeholder, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const toggle = (v) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  const filtered = (options || []).filter((o) => {
    if (!q) return true;
    const text = (o.name || o.label || o.value || o).toString().toLowerCase();
    return text.includes(q.toLowerCase());
  });
  return (
    <div className="relative">
      <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">{label}{required && ' *'}</label>
      <button type="button" onClick={() => setOpen(!open)} className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white text-left focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none" data-testid={testid}>
        {values.length === 0 ? <span className="text-gray-400">{placeholder}</span> : <span className="text-[#1A2A4F]">{values.length} selected</span>}
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-hidden" data-testid={`${testid}-dropdown`}>
          <div className="p-2 border-b border-gray-100">
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full text-sm px-2 py-1 outline-none" />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? <p className="p-3 text-xs text-gray-500">No matches.</p> : filtered.map((o) => {
              const v = o.value || o.name || o;
              const lbl = o.name || o.label || o;
              const checked = values.includes(v);
              return (
                <label key={v} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                  <input type="checkbox" checked={checked} onChange={() => toggle(v)} className="accent-[#F5A623]" />
                  <span className="text-[#1A2A4F]">{lbl}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
      {values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {values.map((v) => (<span key={v} className="text-xs bg-[#F5A623]/10 text-[#F5A623] px-2 py-0.5 rounded">{v}</span>))}
        </div>
      )}
    </div>
  );
};


// ─────────────── Student Form ───────────────
const StudentForm = ({ countries, onSuccess }) => {
  const { login } = useAuth();
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [f, setF] = useState({
    first_name: '', last_name: '', email: '', currency: '', mobile: '',
    password: '', nationality: '', gender: 'Male',
    country: '', city: '', postal_code: '',
  });

  const update = (k) => (v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!otpSent) { setError('Please send and enter the verification code first.'); return; }
    if (otp.length !== 6) { setError('Please enter the 6-digit verification code.'); return; }
    setLoading(true); setError('');
    try {
      const r = await axios.post(`${API}/ebn/auth/register/student`, { ...f, otp });
      localStorage.setItem('token', r.data.access_token);
      const next = sessionStorage.getItem('ebn_next_after_login');
      sessionStorage.removeItem('ebn_next_after_login');
      // Hard reload so AuthProvider re-initialises with the new token,
      // fetches /auth/me and shows the logged-in header.
      // Send the student to the EduBridge landing (or wherever they were
      // headed before signup), NOT the legacy /student portal.
      window.location.href = next && !next.startsWith('/student') ? next : '/';
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create account. Please try again.');
    }
    setLoading(false);
  };

  return (
    <form onSubmit={submit} className="mt-6 space-y-8" data-testid="student-register-form">
      <fieldset>
        <legend className="text-lg font-bold text-[#1A2A4F] mb-4">Basic Information</legend>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input label="First name" required value={f.first_name} onChange={update('first_name')} placeholder="Your first name" testid="student-first-name" />
          <Input label="Last name" required value={f.last_name} onChange={update('last_name')} placeholder="Your last name" testid="student-last-name" />
          <Input label="Email address" required type="email" value={f.email} onChange={update('email')} placeholder="Enter your email address" testid="student-email" />

          <Select label="Currency you Use" required value={f.currency} onChange={update('currency')} options={CURRENCIES} placeholder="Select Currency" testid="student-currency" />
          <Input label="Mobile Number" value={f.mobile} onChange={update('mobile')} placeholder="+ Enter mobile number" testid="student-mobile" />

          <div>
            <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">Password *</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                required
                value={f.password}
                onChange={(e) => setF({ ...f, password: e.target.value })}
                placeholder="Enter your password"
                className="w-full border border-gray-200 rounded-md px-3 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none"
                data-testid="student-password"
                minLength={6}
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#F5A623]" data-testid="student-pw-toggle">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Select label="Nationality" required value={f.nationality} onChange={update('nationality')} options={countries.map((c) => c.name)} placeholder="Select Nationality" testid="student-nationality" />
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">Gender</label>
            <div className="flex gap-5 mt-2">
              {GENDERS.map((g) => (
                <label key={g} className="flex items-center gap-2 text-sm text-[#1A2A4F]">
                  <input type="radio" name="gender" value={g} checked={f.gender === g} onChange={() => setF({ ...f, gender: g })} className="accent-[#F5A623]" data-testid={`student-gender-${g.toLowerCase()}`} />
                  {g}
                </label>
              ))}
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">For dual nationality, enter the most relevant country of nationality for the program you want to apply for.</p>
      </fieldset>

      <fieldset>
        <legend className="text-lg font-bold text-[#1A2A4F] mb-4">Residence</legend>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select label="Country living in" required value={f.country} onChange={update('country')} options={countries.map((c) => c.name)} placeholder="Select Country" testid="student-country" />
          <Input label="City/Town" required value={f.city} onChange={update('city')} placeholder={f.country ? 'Enter your city/town' : 'Select Country First'} testid="student-city" />
          <Input label="Postal/Zip code" value={f.postal_code} onChange={update('postal_code')} placeholder="Enter Postal Code (optional)" testid="student-postal" />
        </div>
      </fieldset>

      {error && <p className="text-sm text-red-600" data-testid="student-error">{error}</p>}

      <OtpStep email={f.email} otp={otp} setOtp={setOtp} sent={otpSent} setSent={setOtpSent} error={error} setError={setError} />

      <div className="flex items-center justify-between gap-4 pt-2">
        <Link to="/login" className="text-sm font-semibold text-[#F5A623] hover:underline" data-testid="student-already">Already have an account?</Link>
        <button
          type="submit"
          disabled={loading || !otpSent || otp.length !== 6}
          className="bg-[#F5A623] text-white font-bold px-7 py-3 rounded-md hover:brightness-95 disabled:opacity-50 transition"
          data-testid="student-submit-btn"
        >
          {loading ? 'Creating…' : 'Verify & Create your account'}
        </button>
      </div>
    </form>
  );
};

// ─────────────── Agent Form ───────────────
const AgentForm = ({ universities, countries }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [f, setF] = useState({
    username: '', email: '', mobile: '',
    universities: [], countries: [], agency_name: '',
  });
  const update = (k) => (v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!otpSent) { setError('Please send and enter the verification code first.'); return; }
    if (otp.length !== 6) { setError('Please enter the 6-digit verification code.'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/ebn/auth/register/agent`, { ...f, otp });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create account. Please try again.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="mt-8 text-center py-10" data-testid="agent-register-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9" />
        </div>
        <h2 className="mt-4 text-2xl font-bold text-[#1A2A4F]">Application received — pending admin approval</h2>
        <p className="mt-3 text-gray-600 max-w-md mx-auto">
          Thanks for signing up as an agent. Our admissions team reviews every agent application manually.
          You'll receive a welcome email with your login details as soon as you're approved (usually within 24 hours).
        </p>
        <Link to="/" className="mt-6 inline-block bg-[#1A2A4F] text-white font-semibold px-7 py-3 rounded-md hover:opacity-90" data-testid="agent-back-home">Back to home</Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-6 space-y-8" data-testid="agent-register-form">
      <fieldset>
        <legend className="text-lg font-bold text-[#1A2A4F] mb-4">Basic Information</legend>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input label="User Name" required value={f.username} onChange={update('username')} placeholder="Your User Name" testid="agent-username" />
          <Input label="Email address" required type="email" value={f.email} onChange={update('email')} placeholder="Enter your email address" testid="agent-email" />
          <Input label="Mobile Number" value={f.mobile} onChange={update('mobile')} placeholder="+ Enter mobile number" testid="agent-mobile" />

          <MultiSelect label="Universities" required values={f.universities} onChange={update('universities')} options={universities.map((u) => ({ name: u.name, value: u.name }))} placeholder="Select University" testid="agent-universities" />
          <MultiSelect label="Countries" required values={f.countries} onChange={update('countries')} options={countries.map((c) => ({ name: c.name, value: c.name }))} placeholder="Select Country" testid="agent-countries" />
          <Input label="Name of Agency" required value={f.agency_name} onChange={update('agency_name')} placeholder="Your Agency Name" testid="agent-agency-name" />
        </div>
      </fieldset>

      {error && <p className="text-sm text-red-600" data-testid="agent-error">{error}</p>}

      <OtpStep email={f.email} otp={otp} setOtp={setOtp} sent={otpSent} setSent={setOtpSent} error={error} setError={setError} />

      <div className="flex items-center justify-between gap-4 pt-2">
        <Link to="/login" className="text-sm font-semibold text-[#F5A623] hover:underline" data-testid="agent-already">Already have an account?</Link>
        <button
          type="submit"
          disabled={loading || !otpSent || otp.length !== 6}
          className="bg-[#F5A623] text-white font-bold px-7 py-3 rounded-md hover:brightness-95 disabled:opacity-50 transition"
          data-testid="agent-submit-btn"
        >
          {loading ? 'Submitting…' : 'Verify & Create your account'}
        </button>
      </div>
    </form>
  );
};

export default RegisterPage;
